import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Upload, X, FileText, Trash2 } from 'lucide-react';
import { CustomDatePicker } from '../ui/custom-date-picker';
import { CATEGORIES, EMPLOYEE_SUB_CATEGORIES, PAYMENT_METHODS, STATUSES, getCategoryConfig, preventWheelChange } from './constants';
import { getTodayString } from '../../utils/dateUtils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

export function EditExpenseDialog({ open, onOpenChange, expense, users, employees, isApprover, onSaved }) {
  const [editForm, setEditForm] = useState({});
  const [nonGstBillFiles, setNonGstBillFiles] = useState([]); // New non-GST files to upload
  const [gstBillFiles, setGstBillFiles] = useState([]); // New GST files to upload
  const [removeExistingGstBills, setRemoveExistingGstBills] = useState(false);
  const [removeExistingNonGstBills, setRemoveExistingNonGstBills] = useState(false);

  // Approver-only date window (today + last 2 IST days). Super approvers
  // bypass. Kept identical to the server-side guard in
  // /app/backend/routes/expenses.py::_enforce_approver_date_window.
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperApprover = currentUser.role === 'super_approver';
  const approverDateFloor = (() => {
    if (isSuperApprover) return null;
    if (currentUser.role !== 'approver') return null;
    const d = new Date();
    d.setDate(d.getDate() - 2);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();
  // If the currently-loaded expense is already dated before the approver's
  // window, they can't edit it at all — flag the dialog so the Update button
  // shows a helpful message and the server 422 is preempted.
  const expenseDateLocked = !!(approverDateFloor && expense?.date && expense.date < approverDateFloor);

  // Initialize form when a new expense is opened for editing
  useEffect(() => {
    if (expense && open) {
      setEditForm({
        amount: expense.amount || '',
        date: expense.date || '',
        submitted_by: expense.submitted_by || '',
        submitted_by_id: expense.submitted_by_id || '',
        category: expense.category || 'Material',
        sub_category: expense.sub_category || '',
        vendor_name: expense.vendor_name || '',
        payment_method: expense.payment_method || 'UPI',
        status: expense.status || 'pending',
        notes: expense.notes || '',
        employee_id: expense.employee_id || '',
        charge_per_ton: expense.charge_per_ton || '',
        total_tons: expense.total_tons || '',
        total_distance: expense.total_distance || '',
        reason: expense.reason || '',
        number_of_tons: expense.number_of_tons || '',
        amount_per_ton: expense.amount_per_ton || '',
        offline_amount_per_ton: expense.offline_amount_per_ton || '',
        offline_amount: expense.offline_amount || '',
        online_amount: expense.online_amount || '',
        gst_percentage: expense.gst_percentage || '',
        amount_per_ton_with_gst: expense.amount_per_ton_with_gst || '',
        delivered_tons: expense.delivered_tons || '',
        pending_tons: expense.pending_tons || '',
        gst_filing: expense.gst_filing || '',
        infra_type: expense.infra_type || '',
        infra_description: expense.infra_description || '',
        selected_employees: (() => {
          try {
            const sel = expense.selected_employees;
            if (!sel) return [];
            if (Array.isArray(sel)) return sel;
            if (typeof sel === 'string') return JSON.parse(sel);
            return [];
          } catch { return []; }
        })()
      });
      setNonGstBillFiles([]);
      setGstBillFiles([]);
      setRemoveExistingGstBills(false);
      setRemoveExistingNonGstBills(false);
    }
  }, [expense, open]);

  // Auto-calculate Silica Purchase amounts
  useEffect(() => {
    const currentConfig = getCategoryConfig(editForm.category, editForm.sub_category);
    if (currentConfig.showSilicaFields) {
      const tons = parseFloat(editForm.number_of_tons) || 0;
      const amountPerTon = parseFloat(editForm.amount_per_ton) || 0;
      const offlinePerTon = parseFloat(editForm.offline_amount_per_ton) || 0;
      const gstPercent = parseFloat(editForm.gst_percentage) || 0;

      // offline_amount = tons × offline_amount_per_ton (NO GST)
      const offlineAmount = tons * offlinePerTon;
      // online_amount = tons × (amount_per_ton - offline_amount_per_ton) (before GST)
      const onlineAmountBase = tons * (amountPerTon - offlinePerTon);
      // GST only on online amount
      const onlineAmountWithGst = onlineAmountBase * (1 + gstPercent / 100);
      // Total = offline (no GST) + online (with GST)
      const totalAmount = offlineAmount + onlineAmountWithGst;
      const amountPerTonWithGst = tons > 0 ? totalAmount / tons : 0;

      setEditForm(prev => ({
        ...prev,
        offline_amount: offlineAmount > 0 ? offlineAmount.toString() : '',
        online_amount: onlineAmountBase > 0 ? onlineAmountBase.toString() : '',
        amount: totalAmount > 0 ? totalAmount.toFixed(2) : '',
        amount_per_ton_with_gst: amountPerTonWithGst > 0 ? amountPerTonWithGst.toFixed(2) : ''
      }));
    }
  }, [editForm.number_of_tons, editForm.amount_per_ton, editForm.offline_amount_per_ton, editForm.gst_percentage, editForm.category, editForm.sub_category]);

  const handleDeliveredTonsChange = (value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const totalTons = parseFloat(editForm.number_of_tons) || 0;
      const delivered = parseFloat(value) || 0;
      if (delivered > totalTons && totalTons > 0) {
        toast.error(`Delivered tons cannot exceed ${totalTons} tons`);
        return;
      }
      const pending = Math.max(0, totalTons - delivered);
      setEditForm(prev => ({
        ...prev,
        delivered_tons: value,
        pending_tons: value !== '' && totalTons > 0 ? pending.toFixed(2) : prev.pending_tons
      }));
    }
  };

  const handlePendingTonsChange = (value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const totalTons = parseFloat(editForm.number_of_tons) || 0;
      const pending = parseFloat(value) || 0;
      if (pending > totalTons && totalTons > 0) {
        toast.error(`Pending tons cannot exceed ${totalTons} tons`);
        return;
      }
      const delivered = Math.max(0, totalTons - pending);
      setEditForm(prev => ({
        ...prev,
        pending_tons: value,
        delivered_tons: value !== '' && totalTons > 0 ? delivered.toFixed(2) : prev.delivered_tons
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.gst_filing) {
      toast.error('Please select GST Filing status (Yes or No)');
      return;
    }
    // Check for existing GST bills (both old format and new R2 keys) - unless user wants to remove them
    const hasExistingGstBills = !removeExistingGstBills && ((expense?.gst_bill_filenames?.length > 0) || (expense?.gst_bill_r2_keys?.length > 0));
    // If GST Filing is Yes and no existing bill and no new file uploaded
    if (editForm.gst_filing === 'Yes' && !hasExistingGstBills && gstBillFiles.length === 0) {
      toast.error('GST bill is mandatory when GST Filing is selected as Yes. Please upload the bill.');
      return;
    }
    const currentConfig = getCategoryConfig(editForm.category, editForm.sub_category);
    if (currentConfig.showSilicaFields && !editForm.offline_amount_per_ton) {
      toast.error('Offline Amount per Ton is required for Silica Purchase');
      return;
    }
    // GST Bill file validation (mandatory for Transport From Mine to Plant, Silica Purchase, or when GST Filing = Yes)
    // Only required if no existing GST bill files
    const needsGstBill = currentConfig.showGstBillField || editForm.gst_filing === 'Yes';
    if (needsGstBill && gstBillFiles.length === 0 && !hasExistingGstBills) {
      toast.error('Please upload the GST Bill (mandatory)');
      return;
    }
    
    // Non-GST bill validation - mandatory when GST Filing is No (except for specific categories)
    const hasExistingNonGstBills = !removeExistingNonGstBills && (
      (expense?.bill_filenames?.length > 0) || 
      expense?.bill_filename || 
      expense?.bill_r2_key
    );
    // Check if non-GST bill is required
    const isNonGstBillRequired = () => {
      // Not required for Transport From Mine to Plant or Silica Purchase (they use GST bills)
      if (editForm.category === 'Transport From Mine to Plant' || editForm.category === 'Silica Purchase') return false;
      // When GST Filing is Yes, non-GST bill is NOT mandatory (GST Bill is mandatory instead)
      if (editForm.gst_filing === 'Yes') return false;
      // For cash payment, bill is mandatory
      if (editForm.payment_method === 'Cash') return true;
      // Default based on category config
      return currentConfig.billRequired;
    };
    
    if (isNonGstBillRequired() && nonGstBillFiles.length === 0 && !hasExistingNonGstBills) {
      toast.error('Please upload a Non-GST Bill/Receipt (mandatory for this expense type)');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      Object.keys(editForm).forEach(key => {
        if (editForm[key] !== '' && editForm[key] !== null && editForm[key] !== undefined) {
          if (key === 'selected_employees' && Array.isArray(editForm[key])) {
            formData.append(key, JSON.stringify(editForm[key]));
          } else {
            formData.append(key, editForm[key]);
          }
        }
      });
      // Append non-GST bill files
      if (nonGstBillFiles.length > 0) {
        nonGstBillFiles.forEach((file) => {
          formData.append('file', file);
        });
      }
      // Append multiple GST bill files
      if (gstBillFiles.length > 0) {
        gstBillFiles.forEach((file) => {
          formData.append('gst_bill_files', file);
        });
      }
      // Send flags to remove existing bills
      if (removeExistingGstBills) {
        formData.append('remove_existing_gst_bills', 'true');
      }
      if (removeExistingNonGstBills) {
        formData.append('remove_existing_non_gst_bills', 'true');
      }
      await axios.put(`${API}/expenses/${expense.id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Expense updated successfully');
      onOpenChange(false);
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Failed to update expense';
      if (Array.isArray(detail)) {
        message = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
      } else if (typeof detail === 'string') {
        message = detail;
      }
      toast.error(message);
    }
  };

  const config = getCategoryConfig(editForm.category, editForm.sub_category);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>Edit Expense</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="edit-expense-form">
          {/* Hidden input to capture initial focus and prevent date picker auto-open */}
          <input type="text" className="sr-only" autoFocus aria-hidden="true" tabIndex={-1} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">
                Amount ({'\u20B9'}) *
                {config.showSilicaFields && (
                  <span className="text-xs text-slate-500 ml-2">(Auto-calculated)</span>
                )}
              </Label>
              <Input
                data-testid="edit-expense-amount"
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]*"
                value={editForm.amount}
                onChange={(e) => {
                  if (!config.showSilicaFields) {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setEditForm({ ...editForm, amount: value });
                    }
                  }
                }}
                required
                disabled={config.showSilicaFields}
                className={`h-11 rounded-xl ${config.showSilicaFields ? 'bg-slate-100 cursor-not-allowed' : ''}`}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Date *</Label>
              <CustomDatePicker
                value={editForm.date}
                onChange={(date) => {
                  if (approverDateFloor && date && date < approverDateFloor) {
                    toast.error('Approvers can only set expense dates to today or the last 2 days.');
                    return;
                  }
                  setEditForm({ ...editForm, date: date || '' });
                }}
                min={approverDateFloor || undefined}
                disabled={expenseDateLocked}
                placeholder="Select date"
              />
              {expenseDateLocked && (
                <p className="text-xs text-red-600 mt-1" data-testid="approver-date-locked-hint">
                  This expense is older than 2 days — only a Super Approver can edit it.
                </p>
              )}
              {!expenseDateLocked && approverDateFloor && (
                <p className="text-xs text-slate-500 mt-1">
                  You can only set today or the previous 2 days.
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold">Submitted By *</Label>
            <Select
              value={editForm.submitted_by_id || editForm.submitted_by}
              onValueChange={(value) => {
                const selectedUser = users.find(u => u.user_id === value);
                setEditForm({
                  ...editForm,
                  submitted_by_id: value,
                  submitted_by: selectedUser?.name || editForm.submitted_by
                });
              }}
            >
              <SelectTrigger className="h-11 rounded-xl" data-testid="edit-expense-submitted-by">
                <SelectValue>{editForm.submitted_by || 'Select user'}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Category *</Label>
              <Select
                value={editForm.category}
                onValueChange={(value) => {
                  const categoryConfig = getCategoryConfig(value);
                  setEditForm({ 
                    ...editForm, 
                    category: value, 
                    sub_category: '', 
                    vendor_name: editForm.vendor_name || categoryConfig.defaultVendor || ''
                  });
                }}
              >
                <SelectTrigger className="h-11 rounded-xl" data-testid="edit-expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Payment Method *</Label>
              <Select
                value={editForm.payment_method}
                onValueChange={(value) => setEditForm({ ...editForm, payment_method: value })}
                disabled={config.showSilicaFields}
              >
                <SelectTrigger className={`h-11 rounded-xl ${config.showSilicaFields ? 'bg-slate-100 cursor-not-allowed' : ''}`} data-testid="edit-expense-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => <SelectItem key={method} value={method}>{method}</SelectItem>)}
                </SelectContent>
              </Select>
              {config.showSilicaFields && (
                <p className="text-xs text-slate-500">Payment method is fixed for Silica Purchase</p>
              )}
            </div>
            {/* GST Filing */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">GST Filing *</Label>
              <Select
                value={editForm.gst_filing}
                onValueChange={(value) => setEditForm({ ...editForm, gst_filing: value })}
                disabled={editForm.category === 'Silica Purchase' || editForm.category === 'Transport From Mine to Plant'}
              >
                <SelectTrigger 
                  data-testid="edit-gst-filing-select"
                  className={`h-11 rounded-xl ${(editForm.category === 'Silica Purchase' || editForm.category === 'Transport From Mine to Plant') ? 'bg-slate-100 cursor-not-allowed opacity-70' : ''}`}
                >
                  <SelectValue placeholder="Select GST Filing status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
              {(editForm.category === 'Silica Purchase' || editForm.category === 'Transport From Mine to Plant') && (
                <p className="text-xs text-slate-500">GST Filing is mandatory for this category</p>
              )}
            </div>
          </div>

          {config.showSubCategory && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Sub-Category</Label>
              <Select
                value={editForm.sub_category || ''}
                onValueChange={(value) => setEditForm({ ...editForm, sub_category: value })}
              >
                <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select sub-category" /></SelectTrigger>
                <SelectContent>
                  {EMPLOYEE_SUB_CATEGORIES.map((sub) => <SelectItem key={sub} value={sub}>{sub}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {config.showEmployeeSelect ? (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Employee Name</Label>
              <Select
                value={editForm.employee_id || ''}
                onValueChange={(value) => {
                  const emp = employees.find(e => e.employee_id === value);
                  setEditForm({ ...editForm, employee_id: value, vendor_name: emp?.name || '' });
                }}
              >
                <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => <SelectItem key={emp.employee_id} value={emp.employee_id}>{emp.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : config.showMultiEmployeeSelect ? (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Select Employees for Ration</Label>
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-xl p-2 space-y-1">
                {employees.map((emp) => (
                  <label key={emp.employee_id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={(editForm.selected_employees || []).includes(emp.employee_id)}
                      onChange={(e) => {
                        const current = editForm.selected_employees || [];
                        const updated = e.target.checked
                          ? [...current, emp.employee_id]
                          : current.filter(id => id !== emp.employee_id);
                        setEditForm({ ...editForm, selected_employees: updated, vendor_name: updated.map(id => employees.find(em => em.employee_id === id)?.name).filter(Boolean).join(', ') });
                      }}
                      className="rounded"
                    />
                    {emp.name}
                  </label>
                ))}
              </div>
              {(editForm.selected_employees || []).length > 0 && (
                <p className="text-xs text-slate-500">{editForm.selected_employees.length} selected — ₹{(parseFloat(editForm.amount || 0) / editForm.selected_employees.length).toFixed(0)} each</p>
              )}
            </div>
          ) : config.vendorField ? (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">{config.vendorField} *</Label>
              <Input
                data-testid="edit-expense-vendor"
                value={editForm.vendor_name}
                onChange={(e) => setEditForm({ ...editForm, vendor_name: e.target.value })}
                required
                disabled={!config.vendorEditable}
                className={`h-11 rounded-xl ${!config.vendorEditable ? 'bg-slate-100' : ''}`}
              />
            </div>
          ) : null}

          {config.showReason && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Reason</Label>
              <Input
                value={editForm.reason || ''}
                onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                className="h-11 rounded-xl"
              />
            </div>
          )}

          {/* Silica Purchase Fields */}
          {config.showSilicaFields && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Number of Tons *</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={editForm.number_of_tons || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setEditForm({ ...editForm, number_of_tons: value });
                      }
                    }}
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Amount per Ton ({'\u20B9'}) *</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={editForm.amount_per_ton || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setEditForm({ ...editForm, amount_per_ton: value });
                      }
                    }}
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Offline Amount per Ton ({'\u20B9'}) *</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={editForm.offline_amount_per_ton || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setEditForm({ ...editForm, offline_amount_per_ton: value });
                      }
                    }}
                    className="h-11 rounded-xl"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Offline Amount ({'\u20B9'}) <span className="text-xs text-slate-500">(Auto-calculated)</span></Label>
                  <Input
                    type="text"
                    value={editForm.offline_amount || ''}
                    disabled
                    className="h-11 rounded-xl bg-slate-100 cursor-not-allowed"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Online Amount ({'\u20B9'}) <span className="text-xs text-slate-500">(Auto-calculated)</span></Label>
                  <Input
                    type="text"
                    value={editForm.online_amount || ''}
                    disabled
                    className="h-11 rounded-xl bg-slate-100 cursor-not-allowed"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">GST % *</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={editForm.gst_percentage || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (/^\d*\.?\d*$/.test(value) && parseFloat(value) <= 100)) {
                      setEditForm({ ...editForm, gst_percentage: value });
                    }
                  }}
                  placeholder="0, 5, 12, 18, 28"
                  className="h-11 rounded-xl"
                  required
                />
                <p className="text-xs text-slate-500">GST will be added to Amount automatically (enter 0 if no GST)</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Amount per Ton with GST ({'\u20B9'}) <span className="text-xs text-slate-500">(Auto-calculated)</span></Label>
                <Input
                  type="text"
                  value={editForm.amount_per_ton_with_gst ? `\u20B9${parseFloat(editForm.amount_per_ton_with_gst).toFixed(2)}` : ''}
                  disabled
                  className="h-11 rounded-xl bg-slate-100 cursor-not-allowed"
                />
              </div>
              {/* Delivered and Pending Tons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Delivered Tons *</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={editForm.delivered_tons || ''}
                    onChange={(e) => handleDeliveredTonsChange(e.target.value)}
                    placeholder="0.00"
                    className="h-11 rounded-xl"
                  />
                  <p className="text-xs text-slate-500">Auto-calculates pending tons</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Pending Tons *</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={editForm.pending_tons || ''}
                    onChange={(e) => handlePendingTonsChange(e.target.value)}
                    placeholder="0.00"
                    className="h-11 rounded-xl"
                  />
                  <p className="text-xs text-slate-500">Auto-calculates delivered tons</p>
                </div>
              </div>
            </>
          )}

          {config.showTransportFields && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Charge per Ton ({'\u20B9'})</Label>
                <Input
                  type="number"
                  value={editForm.charge_per_ton || ''}
                  onChange={(e) => setEditForm({ ...editForm, charge_per_ton: e.target.value })}
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Total Tons</Label>
                <Input
                  type="number"
                  value={editForm.total_tons || ''}
                  onChange={(e) => setEditForm({ ...editForm, total_tons: e.target.value })}
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Total Distance (km)</Label>
                <Input
                  type="number"
                  value={editForm.total_distance || ''}
                  onChange={(e) => setEditForm({ ...editForm, total_distance: e.target.value })}
                  className="h-11 rounded-xl"
                />
              </div>
            </div>
          )}

          {/* Infra Development Fields */}
          {editForm.category === 'Infra Development' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Infrastructure Type</Label>
                <Select
                  value={editForm.infra_type || ''}
                  onValueChange={(value) => setEditForm({ ...editForm, infra_type: value })}
                >
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="upgrade">Upgrade</SelectItem>
                    <SelectItem value="new">New Construction</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Infra Reason/Description</Label>
                <Input
                  type="text"
                  value={editForm.infra_reason || editForm.infra_description || ''}
                  onChange={(e) => setEditForm({ ...editForm, infra_reason: e.target.value })}
                  placeholder="Describe the infrastructure work"
                  className="h-11 rounded-xl"
                />
              </div>
            </div>
          )}

          {/* GST Bill File Upload - Shows when GST Filing = Yes or for specific categories */}
          {(editForm.gst_filing === 'Yes' || config.showGstBillField) && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">GST Bill(s) *</Label>
              
              {/* Show existing GST bills if any (check both old format and new R2 keys) */}
              {!removeExistingGstBills && ((expense?.gst_bill_filenames?.length > 0) || (expense?.gst_bill_r2_keys?.length > 0)) && (
                <div className="bg-green-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-700">Existing GST Bills</span>
                    <button
                      type="button"
                      onClick={() => setRemoveExistingGstBills(true)}
                      className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Remove All
                    </button>
                  </div>
                  <div className="space-y-1">
                    {(expense?.gst_bill_filenames || []).map((filename, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-slate-700">
                        <FileText className="w-4 h-4 text-green-600" />
                        <span className="truncate">{filename}</span>
                      </div>
                    ))}
                    {(!expense?.gst_bill_filenames || expense?.gst_bill_filenames?.length === 0) && expense?.gst_bill_r2_keys?.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <FileText className="w-4 h-4 text-green-600" />
                        <span>{expense.gst_bill_r2_keys.length} file(s) stored</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Show removal notice */}
              {removeExistingGstBills && ((expense?.gst_bill_filenames?.length > 0) || (expense?.gst_bill_r2_keys?.length > 0)) && (
                <div className="bg-red-50 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-sm text-red-600">Existing GST bills will be removed on save</span>
                  <button
                    type="button"
                    onClick={() => setRemoveExistingGstBills(false)}
                    className="text-xs text-blue-500 hover:text-blue-700"
                  >
                    Undo
                  </button>
                </div>
              )}
              
              {/* Upload new GST bills */}
              <div className={`border-2 border-dashed rounded-xl p-4 text-center transition-all cursor-pointer ${
                gstBillFiles.length === 0 && !(!removeExistingGstBills && (expense?.gst_bill_filenames?.length || expense?.gst_bill_r2_keys?.length))
                  ? 'border-red-300 hover:border-red-400 bg-red-50/50'
                  : 'border-green-300 bg-green-50/50'
              }`}>
                <input
                  id="edit_gst_bill_files"
                  data-testid="edit-gst-bill-files-input"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  multiple
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const newFiles = Array.from(e.target.files);
                      const validFiles = newFiles.filter(file => {
                        if (file.size > 10 * 1024 * 1024) {
                          toast.error(`File "${file.name}" exceeds 10MB limit`);
                          return false;
                        }
                        return true;
                      });
                      setGstBillFiles(prev => [...prev, ...validFiles]);
                    }
                  }}
                  className="hidden"
                />
                <label htmlFor="edit_gst_bill_files" className="cursor-pointer flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100">
                    <Upload className="w-4 h-4 text-slate-500" />
                  </div>
                  <span className="text-sm font-medium text-slate-600">
                    {gstBillFiles.length > 0 ? `${gstBillFiles.length} new file(s) selected` : 'Click to upload new GST Bill(s)'}
                  </span>
                  <span className="text-xs text-slate-500">PDF, PNG, JPG (Max 10MB each)</span>
                </label>
              </div>
              
              {/* New GST Bill Files List */}
              {gstBillFiles.length > 0 && (
                <div className="space-y-2 mt-3">
                  {gstBillFiles.map((f, index) => (
                    <div key={index} className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-slate-700 truncate max-w-[200px]">{f.name}</span>
                        <span className="text-xs text-slate-400">({(f.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setGstBillFiles(prev => prev.filter((_, i) => i !== index))}
                        className="p-1 hover:bg-red-100 rounded-full transition-colors"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setGstBillFiles([])}
                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Remove All New Files
                  </button>
                </div>
              )}
            </div>
          )}

          {isApprover && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm({ ...editForm, status: value })}
              >
                <SelectTrigger className="h-11 rounded-xl" data-testid="edit-expense-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-sm font-semibold">Notes</Label>
            <Textarea
              data-testid="edit-expense-notes"
              value={editForm.notes || ''}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              rows={2}
              className="rounded-xl resize-none"
            />
          </div>

          {/* Non-GST Bills/Receipts Section */}
          {(() => {
            // Determine if non-GST bill is required
            const isNonGstRequired = () => {
              if (editForm.category === 'Transport From Mine to Plant' || editForm.category === 'Silica Purchase') return false;
              if (editForm.gst_filing === 'Yes') return false;
              if (editForm.payment_method === 'Cash') return true;
              return config.billRequired;
            };
            const nonGstRequired = isNonGstRequired();
            const hasExistingNonGstBills = !removeExistingNonGstBills && (
              (expense?.bill_filenames?.length > 0) || 
              expense?.bill_filename || 
              expense?.bill_r2_key
            );
            
            return (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">
                  Non-GST Bills/Receipts {nonGstRequired && <span className="text-red-500">*</span>}
                </Label>
                
                {/* Show existing non-GST bills if any */}
                {!removeExistingNonGstBills && ((expense?.bill_filenames?.length > 0) || expense?.bill_filename || expense?.bill_r2_key) && (
              <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-700">Existing Non-GST Bills</span>
                  <button
                    type="button"
                    onClick={() => setRemoveExistingNonGstBills(true)}
                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Remove All
                  </button>
                </div>
                <div className="space-y-1">
                  {/* Handle array of filenames (new format) */}
                  {expense?.bill_filenames?.length > 0 && expense.bill_filenames.map((filename, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-slate-700">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="truncate">{filename}</span>
                    </div>
                  ))}
                  {/* Handle single filename (old format) */}
                  {!expense?.bill_filenames?.length && expense?.bill_filename && (
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="truncate">{expense.bill_filename}</span>
                    </div>
                  )}
                  {/* Handle R2 key only (no filename stored) */}
                  {!expense?.bill_filenames?.length && !expense?.bill_filename && expense?.bill_r2_key && (
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span>1 file stored</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Show removal notice */}
            {removeExistingNonGstBills && ((expense?.bill_filenames?.length > 0) || expense?.bill_filename || expense?.bill_r2_key) && (
              <div className="bg-red-50 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-red-600">Existing non-GST bills will be removed on save</span>
                <button
                  type="button"
                  onClick={() => setRemoveExistingNonGstBills(false)}
                  className="text-xs text-blue-500 hover:text-blue-700"
                >
                  Undo
                </button>
              </div>
            )}
            
            {/* Upload new non-GST bills */}
            <div className={`border-2 border-dashed rounded-xl p-4 text-center transition-all cursor-pointer border-slate-200 hover:border-blue-400 hover:bg-blue-50/50`}>
              <input
                type="file"
                id="edit-non-gst-files"
                multiple
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    const newFiles = Array.from(e.target.files);
                    const validFiles = newFiles.filter(file => {
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error(`File "${file.name}" exceeds 10MB limit`);
                        return false;
                      }
                      return true;
                    });
                    setNonGstBillFiles(prev => [...prev, ...validFiles]);
                  }
                }}
                className="hidden"
              />
              <label htmlFor="edit-non-gst-files" className="cursor-pointer flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100">
                  <Upload className="w-4 h-4 text-slate-500" />
                </div>
                <span className="text-sm font-medium text-slate-600">
                  {nonGstBillFiles.length > 0 ? `${nonGstBillFiles.length} new file(s) selected` : 'Click to upload new Non-GST Bill(s)'}
                </span>
                <span className="text-xs text-slate-500">{nonGstRequired ? 'Mandatory' : 'Optional'} • PDF, PNG, JPG (Max 10MB each)</span>
              </label>
            </div>
            
            {/* New non-GST bill files list */}
            {nonGstBillFiles.length > 0 && (
              <div className="space-y-2 mt-3">
                {nonGstBillFiles.map((f, index) => (
                  <div key={index} className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-slate-700 truncate max-w-[200px]">{f.name}</span>
                      <span className="text-xs text-slate-400">({(f.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNonGstBillFiles(prev => prev.filter((_, i) => i !== index))}
                      className="p-1 hover:bg-red-100 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setNonGstBillFiles([])}
                  className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Remove All New Files
                </button>
              </div>
            )}
          </div>
            );
          })()}

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-11 rounded-xl" data-testid="edit-expense-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={expenseDateLocked}
              className="flex-1 h-11 bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="edit-expense-submit"
              title={expenseDateLocked ? 'This expense is older than 2 days — only a Super Approver can edit it.' : undefined}
            >
              Update Expense
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
